<template>
    <span class="fi fh fa-c ath-visual-number" :class="[data.isEmpty ? 'el--empty' : '']" style="flex-shrink: 0;">
        <template v-if="!data.isEmpty">
            <span class="ath-visual-number-value"
                :class="[data.uiValueCname]">{{ value | athNumber }}</span>
            <Trend v-if="data.trend !== false"
                :value="data.trend"
                class="sm-l-xs"
                :class="[data.uiComparisonCname]"
                :colorize="comparisonColorize" />
            <small v-if="data.trendSub !== false"
                class="te-subnote sm-l-xs"
                :class="[data.uiSubvalueCname]">{{ data.trendSub | athNumber }}</small>
            <small v-if="data.pct !== false"
                class="te-subnote sm-l-xs"
                :class="[data.uiSubvalueCname]">({{ data.pct | athNumber }}%)</small>
            <small v-if="data.sub !== false"
                class="te-subnote sm-l-xs"
                :class="[data.uiSubvalueCname]">({{ data.sub | athNumber }})</small>
        </template>
        <span v-else :class="[data.uiValueCname]">{{ fallback }}</span>
    </span>
</template>

<script>
import { isNeString, isNumber, isNumberGreaterThan, join } from '@aretheyhappy/utils';
import Trend from './Trend.vue';
export default {
    name: 'mead-number',
    props: {
        /**
         * Boolean flag to turn off percentage render. When `false`, the computed
         * percentage (based on `value`/`total`) is never shown, even if `total` is set.
         */
        uiPct               : {type: Boolean, default: () => true},
        /**
         * Boolean flag to turn off subvalue render. When `false`, `subvalue` is
         * never shown, even if provided.
         */
        uiSub               : {type: Boolean, default: () => true},
        /**
         * Boolean flag to turn off trend render. When `false`, no trend arrow is
         * computed or shown, even if `comparison` is provided.
         */
        uiTrend             : {type: Boolean, default: () => true},
        /**
         * Reserved value used for empty comparison. Currently unused by the
         * component's own logic; defaults to `undefined`.
         */
        emptyval            : {type: [Number, Boolean], default: () => undefined},
        /**
         * Value to render when `value` is empty (not a number).
         */
        fallback            : {type: [Number, String], default: () => 0},
        /**
         * The primary number to render. Required to render; when not a number,
         * the component is considered empty and shows `fallback` instead.
         */
        value               : {type: [Number, Boolean], default: () => false},
        /**
         * Custom class(es) applied to the rendered value. When the value is empty,
         * any "te-pfix-pct" or "te-sfix-plus" tokens are stripped out automatically.
         */
        valueCname          : {type: [Boolean, String], default: () => false},
        /**
         * Colorize the value (0 = not colorized, 1 = standard positive/negative
         * coloring, -1 = inverse coloring).
         */
        valueColorize       : {type: Number, default: () => 0},
        /**
         * Pass to render a percentage of `value` out of `total`
         * (only shown when `uiPct` is true and this is a positive number).
         */
        total               : {type: [Number, Boolean], default: () => false},
        /**
         * Pass to render a subvalue in parentheses next to the main value
         * (only shown when `uiSub` is true, `comparisonAsSub` is false, and no
         * percentage/comparison-as-sub is being rendered instead).
         */
        subvalue            : {type: [Number, Boolean], default: () => false},
        /**
         * Custom class(es) applied to the rendered subvalue/comparison/percentage note.
         */
        subvalueCname       : {type: [Boolean, String], default: () => false},
        /**
         * Pass to render a trend indicator, computed as the difference between
         * `value` and this comparison value (only shown when `uiTrend` is true).
         */
        comparison          : {type: [Number, Boolean], default: () => false},
        /**
         * Custom class(es) applied to the rendered trend indicator.
         */
        comparisonCname     : {type: [Boolean, String], default: () => false},
        /**
         * Colorize the trend/comparison (0 = not colorized, 1 = standard
         * positive/negative coloring, -1 = inverse coloring).
         */
        comparisonColorize  : {type: Number, default: () => 1},
        /**
         * Use the computed trend value as the subvalue display instead of `subvalue`.
         * When true, `subvalue` is not rendered.
         */
        comparisonAsSub     : {type: Boolean, default: () => false},
        /**
         * Calculate an inverse trend (flips the sign of `value - comparison`
         * before computing the trend direction).
         */
        comparisonInverse   : {type: Boolean, default: () => false},
    },
    components: {Trend},
    computed: {
        data: function () {
            const isEmpty = !isNumber(this.value);

            //  Computed trend, only rendered if comparison is also provided
            const trend = isEmpty || !this.uiTrend || !isNumber(this.comparison)
                ? false
                : (this.comparisonInverse ? -1 : 1) * (this.value - this.comparison);

            //  Computed trend sub, only rendered if comparisonAsSub is set to true and we have a comparison
            const trendSub = !this.comparisonAsSub || !trend
                ? false
                : trend;

            //  Computed percentage, only rendered if total is also provided
            const pct = isEmpty || !this.uiPct || !isNumberGreaterThan(this.total, 0)
                ? false
                // This value is defined in webapp and uses a company setting to work -_- 
                // eslint-disable-next-line no-undef
                : ATH.UTIL.Math.floatToPercent(this.value/this.total);

            //  Compute subvalue, only rendered if pct is not rendered
            const sub = isEmpty || pct !== false || trendSub !== false || !this.uiSub || !isNumber(this.subvalue)
                ? false
                : this.subvalue;

            //  Compute value cname
            let value_cname = '';
            if (isNeString(this.valueCname)) {
                value_cname = isEmpty ? this.valueCname.replace('te-pfix-pct', '').replace('te-sfix-plus', '') : this.valueCname;
            }

            //  Compute color cname
            let value_color = '';
            if (this.valueColorize !== 0 && !isEmpty) {
                value_color = this.valueColorize === -1
                    ? this.value > 0 ? 'el--t-negative' : 'el--t-positive'
                    : this.value > 0 ? 'el--t-positive' : 'el--t-negative';
            }

            return {
                isEmpty,
                trend,
                trendSub,
                pct,
                sub,
                uiValueCname: join([value_cname, value_color], {delim: ' '}),
                uiComparisonCname: isEmpty || !isNeString(this.comparisonCname) ? '' : this.comparisonCname,
                uiSubvalueCname: isEmpty || !isNeString(this.subvalueCname) ? '' : this.subvalueCname,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.ath-visual-trend.fas-r {
    transform: translateY(-.4rem);
}

.el--empty {
    opacity: .25;
}

.ath-visual-number-value {
    &.el--t-positive {
        color: var(--c-sentiment-positive-bg);
    }

    &.el--t-negative {
        color: var(--c-sentiment-negative-bg);
    }
}
</style>
